export interface Group {
  courseName: string;
  id: string;
  name: string;
  programId?: ProgramEnum;
  isLudificationActive: boolean;
  courseId: string;
  itinerary?: OnmatItinerary;
  periods?: PeriodItinerary[];
  students?: StudentsGroup[];
  teachers?: TeacherGroup[];
  externalGroupInfo?: ExternalGroupInfo;
  progress?: GroupProgressItinerary;
  course?: Course;
}

export interface Course {
  id?: string;
  name?: string;
  number?: number;
  title?: string;
  tkCourseCode?: string;
  programId?: ProgramEnum;
}

export interface GroupCreationData {
  id?: string;
  isLudificationActive?: boolean;
  courseId: string;
  courseName: string;
  name: string;
  programId?: ProgramEnum;
  academicYearId: string;
  externalGroupInfo?: ExternalGroupInfo;
  teachers: TeacherCreationData[];
  students?: StudentsGroup[];
}

export interface AlreadyImportedGroup {
  /** Id of the external class (Classroom / Teams) that was being imported */
  externalGroupId: string;
  /** Id of the ONMAT class that already holds it, as returned by the API in the 400 body */
  groupId?: string;
}

export interface ExternalGroupInfo {
  id: string;
  externalGroupDiscriminator: ExternalGroupInfoDiscriminator;
}

export interface StudentCreationData {
  userName: string;
  name?: string;
  firstLastName?: string;
  secondLastName?: string;
}

export interface TeacherCreationData {
  userName: string;
  teacherId?: string;
  name?: string;
  lastName?: string;
  tkUserId?: string;
  status?: number;
}

export interface School {
  ActiveAcademicYear: null;
  TimePeriods: null;
  Id: string;
  Name: string;
  CompetencePonderationsVisible: boolean;
  ActivityPonderationsVisible: boolean;
  Region: string;
  ActiveAcademicYearId: string;
}

export class GroupGrades {
  subCompetenceId: string;
  grade: number | string;
}

export class SelectedStudent {
  id: string;
  name: string;
  activityStatus: number;
  grade1: number | string;
  grade2: number | string;
  subCompetenceGrade?: GroupGrades[];

  constructor(
    id: string,
    name: string,
    activityStatus: number,
    grade1: number | string,
    grade2: number | string,
    subCompetenceGrade?: GroupGrades[]
  ) {
    this.id = id;
    this.name = name;
    this.activityStatus = activityStatus;
    this.grade1 = grade1;
    this.grade2 = grade2;
    this.subCompetenceGrade = subCompetenceGrade;
  }
}

export interface GroupStudent {
  ActivityAttendance: null;
  DayActivities: null;
  PastTeamRoles: null;
  School: School;
  StudentAnswers: null;
  StudentGroups: any[];
  StudentTeams: null;
  User: null;
  Id: string;
  UserId: string;
  TkStudentId: null;
  SchoolId: string;
  IsLudificationActive: boolean;
  Alias: null;
  Avatar: null;
  Name: null;
  Surname: null;
  Photo: null;
}

export interface RegistrableStudent {
  userName: string;
  name?: string;
  firstLastName?: string;
  secondLastName?: string;
}

export interface TeacherGroup {
  id?: string;
  lastName?: string;
  name?: string;
  userName: string;
  tkUserId?: string;
}

export interface CenterTeacher extends TeacherGroup {
  teacherId: string;
  groups: GroupDetail[];
  status: number;
  tkUserId: string;
}

export interface Teacher {
  School: School | null;
  TeacherGroups: null;
  User: null;
  Id: string;
  UserId: string;
  TkTeacherId: null;
  SchoolId: null | string;
  Name: string;
  Surname: string;
  Photo: string;
}

export interface StudentsGroup {
  id?: string;
  name?: string;
  lastName?: string;
  status?: number;
  userName?: string;
  password?: string;
  tkUserId?: string;
}

export class StudentModel implements StudentsGroup {
  public id: string;
  public name: string;
  public lastName: string;
  public userName: string;
  public status: number;

  constructor(data: Partial<StudentModel> = {}) {
    this.name = data.name || '';
    this.lastName = data.lastName || '';
    this.userName = data.userName || '';
  }

  public static studentFromGoogleClassroom(student: StudentsGoogleImported): StudentModel {
    return new StudentModel({
      id: student.userId,
      name: student.profile.name.givenName,
      lastName: student.profile.name.familyName,
      userName: student.profile.emailAddress,
    });
  }

  public static studentFromTeams(student: StudentsTeamsImported): StudentModel {
    return new StudentModel({
      id: student.id,
      name: student.displayName,
      lastName: student.surname,
      userName: student.mail,
    });
  }
}

export interface StudentsGoogleImported {
  userId: string;
  courseId: string;
  profile?: StudentsImportedProfile;
}

export interface StudentsTeamsImported {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
  mail: string;
}

export interface StudentsImportedProfile {
  id: string;
  emailAddress?: string;
  name?: StudentsImportedProfileName;
}

export interface StudentsImportedProfileName {
  familyName?: string;
  fullName?: string;
  givenName?: string;
}

export interface GroupDetail {
  id: string;
  name: string;
  course: Course;
  externalGroupInfo?: ExternalGroupInfo;
  students: StudentsGroup[];
  teachers: TeacherGroup[];
  studentsPendingActivation: boolean;
  teachersPendingActivation: boolean;
}

export interface Team {
  id: string;
  name: string;
  students: string[];
}

export interface Itinerary {
  academicYearId?: string;
  timePeriods?: number;
  groups?: Array<Group>[];
}

export interface OnmatItinerary {
  id?: string;
  title?: string;
  timePeriods?: PeriodItinerary[];
  periods?: PeriodItinerary[];
}

export interface ItineraryOption {
  id?: number;
  steps?: number;
  title?: string;
  groupId?: string;
  alert?: boolean;
  courseName?: string;
  courseNameTrimmed?: string;
  timePeriods?: number;
}

export interface PeriodItinerary {
  timePeriodTitle?: string;
  order?: number;
  title?: string;
  steps?: ItineraryStep[];
  timePeriods?: ITimePeriod[];
  id?: string;
}

export interface ITimePeriod {
  id?: string;
  order?: number;
  steps?: ItineraryStep[];
  title?: string;
}

export interface ItineraryStep {
  id: string;
  title?: string;
  styleId?: string;
  order?: number;
  period?: number;
  timePeriodTemplateId?: string;
  parentName?: string;
  parentStyleName?: string;
  isCurrent?: boolean;
  isCompleted?: boolean;
  isHidden?: boolean;
}

export interface ItineraryRequest {
  sourceGroupId: string;
  copyActivities?: boolean;
  copyFeatures?: boolean;
}

export interface ItineraryLegend {
  title: string;
  style?: string;
}

export interface GroupProgressItinerary {
  stepId?: string;
  timePeriodTemplateId?: string;
}

export interface Period {
  id?: string;
  order?: number;
  title?: string;
}

export class ExportEvaluation {
  timePeriodTemplateIds?: string[];
  type?: EvaluationExportType;
}

export enum EvaluationExportType {
  CONTENTS = 0,
  COMPETENCES = 1,
  CONTENTS_AND_COMPETENCES = 2,
}

export enum ExternalGroupInfoDiscriminator {
  CLASSROOM = 0,
  TEAMS = 1,
}

export enum ProgramEnum {
  EMAT,
  ONMAT,
}

export interface License {
  courseId: string;
  number: number;
  title: string;
  totalLicenses: number;
}

export interface User {
  role: 'STUDENT' | 'TEACHER';
  userName: string;
  TkUserId: string;
  password: string;
  id?: string;
  result?: number;
}
